import React, { useMemo } from 'react';
import { APPLICATION_STATUSES } from '../constants/crmConstants';

/**
 * SubmitDashboard Component
 * Provides analytical visualization for university applications.
 */
const SubmitDashboard = ({ applicationRecords = [] }) => {
  // 1. Data Aggregation: Status Table
  const statusSummary = useMemo(() => {
    
    return APPLICATION_STATUSES.map(status => ({
      name: status,
      count: applicationRecords.filter(app => app.status === status).length
    }));
  }, [applicationRecords]);

  // 2. Data Aggregation: Weekly Trends Graph
  const chartData = useMemo(() => {
    const last8Weeks = [];
    const now = new Date();
    
    // Generate labels for the last 8 weeks
    for (let i = 7; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - (i * 7));
      // Find start of week (Sunday)
      const day = d.getDay();
      const diff = d.getDate() - day;
      const startOfWeek = new Date(d.setDate(diff));
      
      last8Weeks.push({
        label: startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        timestamp: startOfWeek.getTime(),
        count: 0
      });
    }

    // Group applications into these weeks
    applicationRecords.forEach(app => {
      const appDate = new Date(app.dateApplied).getTime();
      for (let i = 0; i < last8Weeks.length; i++) {
        const weekStart = last8Weeks[i].timestamp;
        const nextWeekStart = i < 7 ? last8Weeks[i+1].timestamp : Infinity;
        
        if (appDate >= weekStart && appDate < nextWeekStart) {
          last8Weeks[i].count++;
          break;
        }
      }
    });

    return last8Weeks;
  }, [applicationRecords]);

  // Logic for Custom Metric Calculations
  const activeStatuses = [
    'App Submitted', 
    'More Info Requested', 
    'Submitted to University', 
    'App Accepted',
    'Offer Shared with Agent',
    'Offer Shared with Student',
    'Offer Signed - Pending Payment', 
    'Offer Signed - Payment Completed'
  ];
  
  const activeCount = applicationRecords.filter(a => activeStatuses.includes(a.status)).length;
  const enrolledCount = applicationRecords.filter(a => a.status === 'Enrolled in Hybrid').length;
  const successRate = applicationRecords.length > 0 
    ? Math.round((enrolledCount / applicationRecords.length) * 100)
    : 0;

  // SVG Chart Constants
  const maxVal = Math.max(...chartData.map(d => d.count), 5); // Minimum scale of 5
  const chartHeight = 200;
  const chartWidth = 800;
  const padding = 40;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Applications Submitted</p>
          <h4 className="text-4xl font-black text-slate-900 mt-2">{applicationRecords.length}</h4>
        </div>
        <div className="bg-[#0464AD] p-8 rounded-[2rem] shadow-xl shadow-blue-100 text-white">
          <p className="text-[10px] font-black text-blue-200 uppercase tracking-widest">Total Active Applications</p>
          <h4 className="text-4xl font-black mt-2">
            {activeCount}
          </h4>
        </div>
        <div className="bg-emerald-500 p-8 rounded-[2rem] shadow-xl shadow-emerald-100 text-white">
          <p className="text-[10px] font-black text-emerald-100 uppercase tracking-widest">Success Rate</p>
          <h4 className="text-4xl font-black mt-2">
            {successRate}%
          </h4>
        </div>
      </div>

      {/* Graph Section */}
      <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm">
        <div className="flex justify-between items-center mb-10">
          <div>
            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">Submissions History</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Weekly submission volume (Last 8 Weeks)</p>
          </div>
        </div>
        
        <div className="relative h-[250px] w-full">
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight + padding}`} className="w-full h-full overflow-visible">
            {/* Grid Lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
              <line 
                key={i} 
                x1="0" 
                y1={chartHeight * p} 
                x2={chartWidth} 
                y2={chartHeight * p} 
                stroke="#f1f5f9" 
                strokeWidth="1" 
              />
            ))}

            {/* Data Line */}
            <polyline
              fill="none"
              stroke="#0464AD"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={chartData.map((d, i) => {
                const x = (chartWidth / (chartData.length - 1)) * i;
                const y = chartHeight - (d.count / maxVal) * chartHeight;
                return `${x},${y}`;
              }).join(' ')}
            />

            {/* Data Points & Labels */}
            {chartData.map((d, i) => {
              const x = (chartWidth / (chartData.length - 1)) * i;
              const y = chartHeight - (d.count / maxVal) * chartHeight;
              return (
                <g key={i}>
                  <circle cx={x} cy={y} r="6" fill="white" stroke="#0464AD" strokeWidth="3" />
                  <text x={x} y={chartHeight + 25} textAnchor="middle" className="text-[10px] font-black fill-slate-400 uppercase tracking-tighter">
                    {d.label}
                  </text>
                  {d.count > 0 && (
                    <text x={x} y={y - 15} textAnchor="middle" className="text-[10px] font-black fill-[#0464AD]">
                      {d.count}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Status Table Section */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-10 py-6 bg-slate-50 border-b border-slate-100">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Application Status Summary</h3>
        </div>
        <table className="w-full text-left border-collapse">
          <thead className="bg-white border-b border-slate-50">
            <tr>
              <th className="px-10 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Application Lifecycle Stage</th>
              <th className="px-10 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total Records</th>
              <th className="px-10 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Distribution</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {statusSummary.map((item, idx) => (
              <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-10 py-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${item.count > 0 ? 'bg-[#0464AD]' : 'bg-slate-200'}`}></div>
                    <span className={`text-sm font-bold ${item.count > 0 ? 'text-slate-800' : 'text-slate-400 font-medium'}`}>{item.name}</span>
                  </div>
                </td>
                <td className="px-10 py-4 text-right">
                  <span className={`text-sm font-black ${item.count > 0 ? 'text-[#0464AD]' : 'text-slate-300'}`}>{item.count}</span>
                </td>
                <td className="px-10 py-4 text-right">
                  <div className="inline-flex items-center gap-2">
                     <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#0464AD] transition-all duration-1000" 
                          style={{ width: `${applicationRecords.length > 0 ? (item.count / applicationRecords.length) * 100 : 0}%` }}
                        ></div>
                     </div>
                     <span className="text-[10px] font-black text-slate-400 w-8">
                       {applicationRecords.length > 0 ? Math.round((item.count / applicationRecords.length) * 100) : 0}%
                     </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubmitDashboard;